import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useOrders } from './OrdersContext';

const LoyaltyContext = createContext(null);
const STORAGE_KEY = 'sb_loyalty_redemptions';

// Rules
export const POINTS_PER_EURO = 10;
export const TIERS = [
  { id: 'bronze', name: 'Bronze', min: 0, color: '#B87333', perk: '5% bonus points' },
  { id: 'silver', name: 'Silver', min: 5000, color: '#C0C0C0', perk: 'Livraison prioritaire' },
  { id: 'gold', name: 'Gold', min: 25000, color: '#FFB300', perk: 'Support VIP 24/7' },
  { id: 'platinum', name: 'Platinum', min: 75000, color: '#B4E1FF', perk: '-10% à vie sur tout' }
];

export const REWARDS = [
  { id: 'r1', cost: 1000, name: '500 Abonnés TikTok', platform: 'TikTok', qty: 500 },
  { id: 'r2', cost: 1500, name: '1 000 Likes Instagram', platform: 'Instagram', qty: 1000 },
  { id: 'r3', cost: 2500, name: '5 000 Vues YouTube', platform: 'YouTube', qty: 5000 },
  { id: 'r4', cost: 4000, name: '10 Avis Google 5★', platform: 'Avis Google', qty: 10 },
  { id: 'r5', cost: 6000, name: '10 000 Spotify Plays', platform: 'Spotify', qty: 10000 },
  { id: 'r6', cost: 12000, name: '10 000 Followers TikTok', platform: 'TikTok', qty: 10000 },
  { id: 'r7', cost: 20000, name: '5 Trustpilot Reviews 5★', platform: 'Trustpilot', qty: 5 },
  { id: 'r8', cost: 35000, name: '25 000 Followers TikTok', platform: 'TikTok', qty: 25000 }
];

const getTier = (points) => {
  let current = TIERS[0];
  for (const t of TIERS) if (points >= t.min) current = t;
  const idx = TIERS.findIndex(t => t.id === current.id);
  const next = TIERS[idx + 1] || null;
  const progress = next ? Math.min(100, ((points - current.min) / (next.min - current.min)) * 100) : 100;
  return { current, next, progress };
};

export const LoyaltyProvider = ({ children }) => {
  const { user } = useAuth();
  const { userOrders, createOrder } = useOrders();

  const [redemptions, setRedemptions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(redemptions));
  }, [redemptions]);

  const userRedemptions = useMemo(
    () => redemptions.filter(r => r.userId === (user?.id || 'guest')),
    [redemptions, user]
  );

  const earned = useMemo(() => Math.round(userOrders.reduce((s, o) => s + o.total, 0) * POINTS_PER_EURO), [userOrders]);
  const spent = useMemo(() => userRedemptions.reduce((s, r) => s + r.cost, 0), [userRedemptions]);
  const balance = Math.max(0, earned - spent);
  const tierInfo = getTier(earned);

  const redeem = useCallback((reward) => {
    if (balance < reward.cost) return { ok: false, error: 'Points insuffisants.' };
    // Log redemption
    const redemption = {
      id: `RD-${Date.now().toString(36).toUpperCase()}`,
      userId: user?.id || 'guest',
      rewardId: reward.id,
      name: reward.name,
      cost: reward.cost,
      createdAt: new Date().toISOString()
    };
    setRedemptions(prev => [redemption, ...prev]);
    // Also generate a live order to track the reward delivery
    createOrder({
      items: [{
        key: `reward-${reward.id}-${Date.now()}`,
        productId: `reward-${reward.id}`,
        name: `🎁 ${reward.name}`,
        platform: reward.platform,
        qty: reward.qty,
        price: 0,
        quantity: 1
      }],
      handle: '@fidélité',
      total: 0
    });
    return { ok: true, redemption };
  }, [balance, user, createOrder]);

  return (
    <LoyaltyContext.Provider value={{ earned, spent, balance, tierInfo, redemptions: userRedemptions, redeem }}>
      {children}
    </LoyaltyContext.Provider>
  );
};

export const useLoyalty = () => useContext(LoyaltyContext);
